import type { SeoAngaben } from '../hooks/useSeo';

/**
 * Titel und Beschreibungen je oeffentlicher Seite.
 *
 * Zentral statt in jeder Komponente: So laesst sich pruefen, dass keine zwei
 * Seiten denselben Titel tragen, und Formulierungen bleiben vergleichbar.
 *
 * Regeln, an denen sich die Texte orientieren:
 *  - Titel unter ~60 Zeichen, sonst kuerzt Google ihn ab.
 *  - Beschreibung 120-160 Zeichen; kuerzere wirken duenn, laengere werden
 *    abgeschnitten.
 *  - Der Name "Saldify" steht in jedem Titel. Google kannte die Marke nicht
 *    und schlug "salsify" vor — Wiederholung ueber alle Seiten hinweg ist das
 *    wirksamste Mittel dagegen.
 *  - Jede Beschreibung nennt, was die Seite konkret bietet. Floskeln wie
 *    "die beste Loesung" ranken nicht und helfen beim Klick nicht.
 */
export const SEO: Record<string, SeoAngaben> = {
  '/': {
    titel: 'Saldify – Online-Buchhaltung für Selbstständige & kleine Unternehmen',
    beschreibung:
      'Rechnungen schreiben, Belege per Foto erfassen, EÜR erstellen und die Umsatzsteuer direkt an ELSTER übermitteln. Saldify bündelt die Buchhaltung in einer Software.',
    pfad: '/',
  },
  '/preise': {
    titel: 'Preise – Saldify Buchhaltungssoftware ab 9 € im Monat',
    beschreibung:
      'Vier Tarife vom kostenlosen Starter bis Business. Alle Preise, Funktionen und Limits im Überblick – monatlich kündbar, ohne versteckte Kosten.',
    pfad: '/preise',
  },
  '/funktionen': {
    titel: 'Funktionen im Überblick – Saldify',
    beschreibung:
      'Rechnungen, Belegerfassung, Banking, Umsatzsteuer, EÜR und BWA: Alle Funktionen von Saldify auf einen Blick, mit Beispielen aus der Praxis.',
    pfad: '/funktionen',
  },
  '/funktionen/rechnungen': {
    titel: 'Rechnungen schreiben – Saldify',
    beschreibung:
      'Rechnungen und Angebote in Minuten erstellen: eigene Nummernkreise, Vorlagen, E-Rechnung (ZUGFeRD/XRechnung), Mahnwesen und Versand per E-Mail.',
    pfad: '/funktionen/rechnungen',
  },
  '/funktionen/belegerfassung': {
    titel: 'Belege per Foto erfassen – Saldify',
    beschreibung:
      'Beleg abfotografieren, den Rest macht die Software: Händler, Datum, Betrag und Steuersatz werden erkannt und dem passenden Konto zugeordnet.',
    pfad: '/funktionen/belegerfassung',
  },
  '/funktionen/banking': {
    titel: 'Bankkonten anbinden & abgleichen – Saldify',
    beschreibung:
      'Umsätze über FinTS automatisch abrufen, Zahlungen passenden Rechnungen zuordnen und offene Posten im Blick behalten – inklusive Mahnwesen.',
    pfad: '/funktionen/banking',
  },
  '/funktionen/umsatzsteuer': {
    titel: 'Umsatzsteuer-Voranmeldung an ELSTER – Saldify',
    beschreibung:
      'UStVA und Umsatzsteuer-Jahreserklärung aus den eigenen Daten berechnen und direkt an ELSTER übermitteln – mit Prüfung vor dem Versand.',
    pfad: '/funktionen/umsatzsteuer',
  },
  '/funktionen/bwa': {
    titel: 'BWA – betriebswirtschaftliche Auswertung – Saldify',
    beschreibung:
      'Monatliche BWA mit Kostengruppen, Umsatzrendite und Vorjahresvergleich. Erkennen, wohin das Geld fließt, ohne auf den Steuerberater zu warten.',
    pfad: '/funktionen/bwa',
  },
  '/funktionen/euer': {
    titel: 'EÜR erstellen – Einnahmenüberschussrechnung – Saldify',
    beschreibung:
      'Die EÜR entsteht laufend aus Belegen und Rechnungen – inklusive AfA, Kassenbuch und Reisekosten. Am Jahresende per ELSTER übermitteln.',
    pfad: '/funktionen/euer',
  },
  '/funktionen/auftraege': {
    titel: 'Angebote & Aufträge verwalten – Saldify',
    beschreibung:
      'Aus dem Angebot wird per Klick eine Rechnung. Status verfolgen, Nachfassen nicht vergessen und den Überblick über offene Aufträge behalten.',
    pfad: '/funktionen/auftraege',
  },
  '/funktionen/kontakte': {
    titel: 'Kunden & Lieferanten verwalten – Saldify',
    beschreibung:
      'Kontakte zentral pflegen: Stammdaten, Zahlungsziele, Rabatte und die komplette Historie an Rechnungen und Belegen je Kontakt.',
    pfad: '/funktionen/kontakte',
  },
  '/funktionen/produkte': {
    titel: 'Produkte & Leistungen – Saldify',
    beschreibung:
      'Wiederkehrende Positionen einmal anlegen und in jeder Rechnung nutzen – mit Preisen, Steuersätzen und optionaler Lagerführung.',
    pfad: '/funktionen/produkte',
  },
  '/funktionen/uebersicht': {
    titel: 'Dashboard & Kennzahlen – Saldify',
    beschreibung:
      'Umsatz, Gewinn, offene Forderungen und Liquidität auf einen Blick – mit Vorjahresvergleich und Auswertung nach Kunden.',
    pfad: '/funktionen/uebersicht',
  },
  '/funktionen/saldenliste': {
    titel: 'Summen- und Saldenliste – Saldify',
    beschreibung:
      'Alle Konten mit Soll, Haben und Saldo je Zeitraum. Grundlage für die Saldenkontrolle und den Jahresabschluss.',
    pfad: '/funktionen/saldenliste',
  },
  '/funktionen/exporte': {
    titel: 'DATEV-Export & Schnittstellen – Saldify',
    beschreibung:
      'Buchungen und Belege für den Steuerberater exportieren – DATEV-Format, CSV und PDF. Der Jahresabschluss bleibt in gewohnten Bahnen.',
    pfad: '/funktionen/exporte',
  },
  '/funktionen/integrationen': {
    titel: 'Integrationen – Stripe, PayPal, SumUp – Saldify',
    beschreibung:
      'Zahlungsdienstleister und Banken anbinden: Auszahlungen, Gebühren und Umsätze fließen automatisch in die Buchhaltung.',
    pfad: '/funktionen/integrationen',
  },
  '/funktionen/mobile-app': {
    titel: 'Mobile App für iOS & Android – Saldify',
    beschreibung:
      'Belege unterwegs fotografieren, Rechnungen prüfen und Zahlungen freigeben – die Daten sind sofort in der Web-Anwendung verfügbar.',
    pfad: '/funktionen/mobile-app',
  },
  '/loesungen/freelancer': {
    titel: 'Buchhaltung für Freelancer – Saldify',
    beschreibung:
      'Für Freiberufler ohne Buchhaltungskenntnisse: Rechnungen, Belege und EÜR in einer Software – mit Umsatzsteuer-Voranmeldung per ELSTER.',
    pfad: '/loesungen/freelancer',
  },
  '/loesungen/kleinunternehmer': {
    titel: 'Buchhaltung für Kleinunternehmer (§19 UStG) – Saldify',
    beschreibung:
      'Rechnungen ohne Umsatzsteuer, Überwachung der Umsatzgrenze und EÜR – Saldify kennt die Regeln für Kleinunternehmer nach §19 UStG.',
    pfad: '/loesungen/kleinunternehmer',
  },
  '/loesungen/startups': {
    titel: 'Buchhaltung für Startups – Saldify',
    beschreibung:
      'Von der Gründung bis zur ersten Finanzierungsrunde: Buchhaltung, Auswertungen und Kennzahlen, die auch Investoren verstehen.',
    pfad: '/loesungen/startups',
  },
  '/loesungen/kmu': {
    titel: 'Buchhaltung für kleine Unternehmen – Saldify',
    beschreibung:
      'Mehrere Mitarbeiter, Bankkonten und Belegberge: Saldify bündelt die laufende Buchhaltung und liefert dem Steuerberater saubere Daten.',
    pfad: '/loesungen/kmu',
  },
  '/loesungen/dienstleister': {
    titel: 'Buchhaltung für Dienstleister – Saldify',
    beschreibung:
      'Projekte abrechnen, Stunden erfassen und wiederkehrende Rechnungen automatisieren – für Agenturen, Berater und Dienstleister.',
    pfad: '/loesungen/dienstleister',
  },
  '/loesungen/ecommerce': {
    titel: 'Buchhaltung für Onlinehandel – Saldify',
    beschreibung:
      'Zahlungen von Stripe, PayPal und SumUp automatisch verbuchen, Gebühren korrekt trennen und die Umsatzsteuer im Griff behalten.',
    pfad: '/loesungen/ecommerce',
  },
  '/loesungen/handwerk': {
    titel: 'Buchhaltung für Handwerksbetriebe – Saldify',
    beschreibung:
      'Angebote, Aufmaß und Rechnungen vom Bau: Belege unterwegs erfassen, Material zuordnen und die Umsatzsteuer korrekt ausweisen.',
    pfad: '/loesungen/handwerk',
  },
  '/steuerberater': {
    titel: 'Für Steuerberater – Zusammenarbeit mit Saldify',
    beschreibung:
      'Eigener Zugang für die Kanzlei, DATEV-Export und saubere Belegablage. Mandanten buchen laufend, die Kanzlei prüft und schließt ab.',
    pfad: '/steuerberater',
  },
  '/api-schnittstelle': {
    titel: 'API-Schnittstelle – Saldify',
    beschreibung:
      'Eigene Systeme anbinden: Rechnungen anlegen, Belege übergeben und Auswertungen abrufen – dokumentiert und versioniert.',
    pfad: '/api-schnittstelle',
  },
  '/hilfe': {
    titel: 'Hilfe & Support – Saldify',
    beschreibung:
      'Antworten auf häufige Fragen zu Rechnungen, Belegen, Umsatzsteuer und ELSTER – und wie du den Support erreichst.',
    pfad: '/hilfe',
  },
  '/blog': {
    titel: 'Blog – Buchhaltung & Steuern verständlich – Saldify',
    beschreibung:
      'Beiträge zu Umsatzsteuer, Kleinunternehmerregelung, GoBD und Jahresabschluss – geschrieben für Selbstständige, nicht für Steuerberater.',
    pfad: '/blog',
  },
  '/updates': {
    titel: 'Produkt-Updates – Saldify',
    beschreibung:
      'Was sich in Saldify ändert: neue Funktionen, Verbesserungen und behobene Fehler, chronologisch dokumentiert.',
    pfad: '/updates',
  },
  '/ueber-uns': {
    titel: 'Über uns – Saldify',
    beschreibung:
      'Wer hinter Saldify steht und warum wir Buchhaltungssoftware bauen, die Selbstständige ohne Vorkenntnisse bedienen können.',
    pfad: '/ueber-uns',
  },
  '/kontakt': {
    titel: 'Kontakt – Saldify',
    beschreibung:
      'Fragen zu Saldify, zur Einrichtung oder zu einem Tarif? So erreichst du uns – per E-Mail oder über das Kontaktformular.',
    pfad: '/kontakt',
  },
  '/partner': {
    titel: 'Partnerprogramm – Saldify weiterempfehlen',
    beschreibung:
      'Saldify empfehlen und profitieren: Für jede Empfehlung, die zu einem Abo führt, gibt es einen Monatsbeitrag als Guthaben.',
    pfad: '/partner',
  },
  '/karriere': {
    titel: 'Karriere – Saldify',
    beschreibung: 'Offene Stellen und wie wir arbeiten. Saldify ist ein kleines Team aus Berlin.',
    pfad: '/karriere',
  },
  '/presse': {
    titel: 'Presse – Saldify',
    beschreibung: 'Pressematerial, Logos und Ansprechpartner für Medienanfragen zu Saldify.',
    pfad: '/presse',
  },
};
